import React, { useEffect, useMemo, useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import {
  buildFullName,
  buildFullNameLower,
  calculateAgeFromDob,
  findDuplicateMatches,
  normalizeSpaces,
  toTitleCase,
} from "../../utils/patient/patientUtils";

const INCIDENT_KEYWORDS = [
  "fall",
  "trip",
  "slip",
  "struck",
  "ball",
  "cart",
  "collision",
  "laceration",
  "cut",
  "injury",
  "head",
  "syncope",
  "faint",
  "chest pain",
  "heat",
  "bee",
  "sting",
  "intox",
];

function clean(value) {
  return String(value || "").trim();
}

function complaintTriggersIncident(value) {
  const text = String(value || "").toLowerCase();
  return INCIDENT_KEYWORDS.some((word) => text.includes(word));
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 3000,
};

const modalStyle = {
  width: "100%",
  maxWidth: 760,
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: 18,
  background: "#f8fafc",
  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
  border: "1px solid #e5e7eb",
};

const sectionStyle = {
  padding: 18,
  borderBottom: "1px solid #e5e7eb",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};

const labelStyle = {
  display: "grid",
  gap: 6,
  fontSize: 14,
  color: "#334155",
  fontWeight: 700,
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  background: "#fff",
  boxSizing: "border-box",
};

const buttonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 14px",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 800,
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#0f172a",
  color: "#fff",
  borderColor: "#0f172a",
};

export default function QuickCheckInModal({
  isOpen,
  call,
  patients = [],
  onClose,
  onCheckInPatient,
}) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    age: "",
    complaint: "",
    incidentRequired: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !call) return;

    setForm({
      firstName: clean(call.hq?.firstName),
      lastName: clean(call.hq?.lastName),
      dob: clean(call.hq?.dob),
      age: "",
      complaint:
        clean(call.hq?.chiefComplaint) ||
        clean(call.nature) ||
        clean(call.dispatchComplaint),
      incidentRequired: "",
    });

    setSaving(false);
    setError("");
  }, [isOpen, call]);

  const matches = useMemo(() => findDuplicateMatches(patients, form), [patients, form]);
  const shouldAskIncident = complaintTriggersIncident(form.complaint);

  function setField(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "dob") {
        next.age = calculateAgeFromDob(value);
      }

      if (field === "complaint") {
        next.incidentRequired = complaintTriggersIncident(value)
          ? prev.incidentRequired
          : "";
      }

      return next;
    });
  }

  async function createNewPatient() {
    const firstName = toTitleCase(form.firstName);
    const lastName = toTitleCase(form.lastName);

    const payload = {
      firstName,
      lastName,
      fullName: buildFullName(firstName, lastName),
      fullNameLower: buildFullNameLower(firstName, lastName),
      dob: normalizeSpaces(form.dob),
      age: normalizeSpaces(form.age),
      gender: "",
      phone: "",
      phoneDigitsOnly: "",
      email: "",
      streetAddress: "",
      city: "",
      state: "",
      zip: "",
      address: "",
      allergies: "",
      allergiesNone: false,
      medications: "",
      medicationsNone: false,
      notes: "",
      lastEncounterAt: null,
      encounterCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdByUid: "",
      createdByName: "",
      updatedByUid: "",
      updatedByName: "",
    };

    const ref = await addDoc(collection(db, "patients"), payload);
    return { id: ref.id, ...payload };
  }

  async function submitWithPatient(patient) {
    setError("");

    if (!clean(form.firstName) || !clean(form.lastName) || !clean(form.dob)) {
      setError("First name, last name, and DOB are required.");
      return;
    }

    if (!clean(form.complaint)) {
      setError("Complaint is required.");
      return;
    }

    if (shouldAskIncident && form.incidentRequired === "") {
      setError("Please answer whether a PGA Incident Report is required.");
      return;
    }

    setSaving(true);

    try {
      await onCheckInPatient?.(patient, {
        firstName: toTitleCase(form.firstName),
        lastName: toTitleCase(form.lastName),
        dob: clean(form.dob),
        age: clean(form.age),
        complaint: clean(form.complaint),
        incidentRequired: shouldAskIncident
          ? form.incidentRequired === "yes"
          : false,
        incidentPrompted: shouldAskIncident,
      });

      onClose?.();
    } catch (err) {
      console.error("Quick check-in failed:", err);
      setError(err?.message || "Could not check in patient.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateAndCheckIn() {
    setError("");

    if (!clean(form.firstName) || !clean(form.lastName) || !clean(form.dob)) {
      setError("First name, last name, and DOB are required.");
      return;
    }

    setSaving(true);

    try {
      const patient = await createNewPatient();
      await submitWithPatient(patient);
    } catch (err) {
      console.error("Create/check-in failed:", err);
      setError(err?.message || "Could not create patient.");
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...sectionStyle, display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 22 }}>Quick Check-In</div>
            <div style={{ color: "#64748b", marginTop: 4 }}>
              Start the chart without opening it. The chart can be opened later.
            </div>
          </div>

          <button type="button" style={buttonStyle} onClick={onClose} disabled={saving}>
            Close
          </button>
        </div>

        <div style={sectionStyle}>
          <div style={gridStyle}>
            <label style={labelStyle}>
              First Name *
              <input
                style={inputStyle}
                value={form.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Last Name *
              <input
                style={inputStyle}
                value={form.lastName}
                onChange={(e) => setField("lastName", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              DOB *
              <input
                style={inputStyle}
                type="date"
                value={form.dob}
                onChange={(e) => setField("dob", e.target.value)}
              />
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>
              Complaint *
              <textarea
                style={{ ...inputStyle, minHeight: 82, resize: "vertical" }}
                value={form.complaint}
                onChange={(e) => setField("complaint", e.target.value)}
              />
            </label>
          </div>
        </div>

        {shouldAskIncident && (
          <div style={{ ...sectionStyle, background: "#fffbeb" }}>
            <div style={{ fontWeight: 900, color: "#92400e", marginBottom: 8 }}>
              This complaint may require a PGA Incident Report.
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setField("incidentRequired", "yes")}
                style={{
                  ...buttonStyle,
                  borderColor: form.incidentRequired === "yes" ? "#f59e0b" : "#fcd34d",
                  background: form.incidentRequired === "yes" ? "#fef3c7" : "#fff",
                  color: "#92400e",
                }}
              >
                Yes, Incident Report Required
              </button>

              <button
                type="button"
                onClick={() => setField("incidentRequired", "no")}
                style={{
                  ...buttonStyle,
                  borderColor: form.incidentRequired === "no" ? "#94a3b8" : "#cbd5e1",
                  background: form.incidentRequired === "no" ? "#e2e8f0" : "#fff",
                  color: "#334155",
                }}
              >
                No
              </button>
            </div>
          </div>
        )}

        {matches.length > 0 && (
          <div style={sectionStyle}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>
              Possible patient matches
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              {matches.map((patient) => (
                <div
                  key={patient.id}
                  style={{
                    border: "1px solid #cbd5e1",
                    borderRadius: 12,
                    background: "#fff",
                    padding: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 900 }}>
                      {patient.fullName || `${patient.firstName || ""} ${patient.lastName || ""}`}
                    </div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      DOB: {patient.dob || "—"}
                    </div>
                  </div>

                  <button
                    type="button"
                    style={primaryButtonStyle}
                    disabled={saving}
                    onClick={() => submitWithPatient(patient)}
                  >
                    Use Match / New Encounter
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            ...sectionStyle,
            borderBottom: "none",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div style={{ color: error ? "#b91c1c" : "#64748b", fontWeight: 700 }}>
            {error || "Check-in creates/links the chart but does not open it."}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" style={buttonStyle} onClick={onClose} disabled={saving}>
              Cancel
            </button>

            <button
              type="button"
              style={primaryButtonStyle}
              onClick={handleCreateAndCheckIn}
              disabled={saving}
            >
              {saving ? "Checking In..." : "Create Patient / Check In"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}